/* eslint-disable indent */
import { Grid, makeStyles, Tab, Tabs } from '@material-ui/core';
import Loading from 'components/Loading';
import i18n from 'i18n';
import { EnvironmentFields } from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import clsx from 'clsx';
import {
  CompanyData,
  RolesEnum,
  PermissionsData,
} from 'modules/authenticate/interfaces';
import {
  inAdminView,
  inOperatorView,
  inSuperAdminView,
  isClientOperator,
} from 'utils/role';
import EnvironmentsTemplate from '../Templates/Environments';
import styles from './styles';
import { ModelsList, ServersList } from '..';
import Publications from '../Publications';

const useStyles = makeStyles(styles);

interface Props {
  companySelected: CompanyData | null;
  environmentSelected: EnvironmentFields | null;
  permissions?: PermissionsData;
  role?: RolesEnum;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const classes = useStyles();

  return (
    <div
      className={classes.tabPanelContent}
      hidden={value !== index}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const Servers = ({
  companySelected,
  environmentSelected,
  permissions,
  role,
}: Props) => {
  const classes = useStyles();
  const [currentTab, setCurrentTab] = React.useState(0);
  const [publicationTab, setPublicationTab] = React.useState('');
  const [highlightSection, setHighlightSection] = React.useState('');

  const isMulticloud = React.useMemo(
    () => environmentSelected?.provider.type === 'multicloud',
    [environmentSelected]
  );

  const configTabs = React.useMemo(
    () =>
      [
        {
          id: 'btn-servers',
          label: i18n.t('SERVERS_KEY'),
          hidden: !permissions?.list_support_servers || isMulticloud,
          component: (
            <ServersList
              setPublicationTab={setPublicationTab}
              setCurrentTab={setCurrentTab}
              setHighlightSection={setHighlightSection}
            />
          ),
        },
        {
          id: 'btn-publications',
          label: i18n.t('PUBLICATIONS_KEY'),
          hidden:
            (!permissions?.publish_image && !permissions?.publish_image_v2) ||
            isMulticloud,
          component: (
            <Publications
              redirectToTab={publicationTab}
              setCurrentTab={setCurrentTab}
              highlightSection={highlightSection}
              setHighlightSection={setHighlightSection}
            />
          ),
        },
        {
          id: 'btn-models',
          label: i18n.t('MODELS_KEY'),
          hidden: !permissions?.list_models || isClientOperator(role),
          component: <ModelsList />,
        },
      ].filter(tab => !tab.hidden),
    [permissions, isMulticloud, role, publicationTab, highlightSection]
  );

  const handleChangeTab = (event: React.ChangeEvent<{}>, newValue: number) => {
    setCurrentTab(newValue);
  };

  const tabClasses = clsx({
    [classes.tabGray]: inSuperAdminView(role, companySelected),
    [classes.tabYellow]: companySelected,
  });

  const tabsClass = clsx({
    [classes.tabsGreen]: inAdminView(role, companySelected),
    [classes.tabsGray]: inSuperAdminView(role, companySelected),
    [classes.tabsBlue]: inOperatorView(role, companySelected),
    [classes.tabsYellow]: companySelected,
  });

  return (
    <EnvironmentsTemplate
      currentPage="servers"
      titlePage={environmentSelected?.name}
      breadcrumbs={[
        { name: i18n.t('ENVIRONMENTS_KEY'), link: '/environments' },
        { name: i18n.t('SERVERS_KEY') },
      ]}
      titleContent={i18n.t('SERVERS_KEY')}
      subtitleContent={`${environmentSelected?.name ??
        i18n.t('LOADING_KEY')} ${i18n.t('SERVERS_KEY')}`}
    >
      <Grid container className={classes.rootTabs}>
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          className={tabsClass}
        >
          {configTabs?.map((tab, index) => (
            <Tab
              key={index}
              id={tab?.id}
              label={tab?.label}
              className={tabClasses}
            />
          ))}
        </Tabs>
      </Grid>

      {environmentSelected ? (
        <Grid container>
          {configTabs?.map((tab, index) => (
            <TabPanel key={index} value={currentTab} index={index}>
              {tab?.component}
            </TabPanel>
          ))}
        </Grid>
      ) : (
        <Loading />
      )}
    </EnvironmentsTemplate>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  companySelected,
  environmentSelected,
  permissions: data?.permissions,
  role: data?.role,
});

export default connect(mapStateToProps, {})(Servers);
